/**
 * Passive effects — pure functions that mutate card objects based on owned
 * passives. Kept isolated from GameScreen so effects can be applied from
 * engine code (initializeGameState, safehouse draft, etc.) as well as the
 * React layer (tuning panel changes).
 *
 * Card-cost reductions floor at 0 (no per-passive minimum — see design doc).
 *
 * IMPORTANT: Call these at card birth or round init, not on every play.
 * Card costs are baked into card instances in this codebase (see Field Modify),
 * so applying passives once at the right seam keeps the rest of the engine
 * blissfully ignorant of passive existence.
 */

import { Card, CardCategory, Player } from '../types/game';
import { NavEdge } from '../types/map';
import { RunState } from '../types/run';
import { hasPassive } from '../data/passives';

/**
 * Return a copy of the card with passive-based cost and range adjustments.
 * Idempotent for a given (card, run) pair.
 *
 * Handles card-cost reductions for:
 *   - Tactician (tactics -1)
 *   - Surveillance State (intel -1)
 *   - Operational Tempo (fieldcraft -1)
 *   - Krav Maga (Improvised Strike → 0)
 *   - Wheelman (Hotwire -1)
 *   - Free Runner (Rooftop Access -1)
 *
 * And movement range bonuses for:
 *   - Long Stride (+50m)
 */
export function applyPassivesToCard(
  card: Card,
  run: RunState | null | undefined,
): Card {
  if (!run || !run.passives || run.passives.length === 0) return card;

  let apCost = card.apCost;
  let movementRange = card.movementRange;

  // --- Card-cost reductions ---
  const category: CardCategory = card.category;

  if (category === 'tactics' && hasPassive(run, 'tactician')) apCost -= 1;
  if (category === 'intel' && hasPassive(run, 'surveillanceState')) apCost -= 1;
  if (category === 'fieldcraft' && hasPassive(run, 'operationalTempo')) apCost -= 1;
  if (card.name === 'Improvised Strike' && hasPassive(run, 'kravMaga')) apCost = 0;
  if (card.name === 'Hotwire' && hasPassive(run, 'wheelman')) apCost -= 1;
  if (card.name === 'Rooftop Access' && hasPassive(run, 'freeRunner')) apCost -= 1;

  apCost = Math.max(0, apCost);

  // --- Long Stride: +50m to any card that has a movement range ---
  if (movementRange !== undefined && hasPassive(run, 'longStride')) {
    movementRange = movementRange + 50;
  }

  // No change — return the original object so reference equality still works
  // for cards that the passives don't touch.
  if (apCost === card.apCost && movementRange === card.movementRange) {
    return card;
  }

  return { ...card, apCost, movementRange };
}

/** Apply passives to every card in an array. */
export function applyPassivesToDeck(
  cards: Card[],
  run: RunState | null | undefined,
): Card[] {
  if (!run || !run.passives || run.passives.length === 0) return cards;
  return cards.map(c => applyPassivesToCard(c, run));
}

/**
 * Starting prep bonus for Cool Head. Returns 0.2 (+20%) if the passive is
 * owned, 0 otherwise. Called from initializeGameState.
 */
export function getStartingPrepBonus(run: RunState | null | undefined): number {
  return hasPassive(run, 'coolHead') ? 0.2 : 0;
}

/**
 * Player hand size including Capacious bonus. Callers should replace
 * `tuning.handSize` with this at the draw sites that affect the player
 * (not the agent).
 */
export function getPlayerHandSize(
  baseHandSize: number,
  run: RunState | null | undefined,
): number {
  return baseHandSize + (hasPassive(run, 'capacious') ? 1 : 0);
}

/**
 * Duration bonus for intel card effects (Sweep, Spotter, Wire Tap, Trace
 * Route). Returns the number of extra turns Persistent Surveillance grants.
 * Callers should add this to any `turnsLeft` / `duration` they set.
 */
export function getIntelDurationBonus(run: RunState | null | undefined): number {
  return hasPassive(run, 'persistentSurveillance') ? 1 : 0;
}

// --- Training bucket: Headhunter intel chain ---

/**
 * Call this from every intel card play handler AFTER the card has been
 * removed from the hand and AP has been deducted. It toggles the chain
 * state and walks the remaining hand to mutate other intel cards' apCost
 * so the *next* intel card played this turn pays 1 less AP.
 *
 * Semantics:
 *   - Called with chain inactive → activates chain, discounts remaining intel by 1
 *   - Called with chain active   → deactivates chain, restores remaining intel
 *
 * The _headhunterDiscounted flag on each card tracks who's been discounted
 * so repeated calls are idempotent for hand cards that were already handled
 * (e.g. a retained-card case, or a new intel card drawn mid-turn).
 *
 * No-op when the player doesn't own Headhunter.
 */
export function applyHeadhunterPostIntelPlay(
  player: Player,
  run: RunState | null | undefined,
): Player {
  if (!hasPassive(run, 'headhunter')) return player;
  const nowActive = !player.intelChainActive;
  return {
    ...player,
    intelChainActive: nowActive,
    hand: player.hand.map(c => {
      if (c.category !== 'intel') return c;
      if (nowActive && !c._headhunterDiscounted) {
        return { ...c, apCost: Math.max(0, c.apCost - 1), _headhunterDiscounted: true };
      }
      if (!nowActive && c._headhunterDiscounted) {
        return { ...c, apCost: c.apCost + 1, _headhunterDiscounted: false };
      }
      return c;
    }),
  };
}

/**
 * Clean up any lingering Headhunter discounts at turn end. Called as part of
 * the turn-end state update so retained (Stash / Hidden Pocket) intel cards
 * don't carry their discount into a new turn where the chain has reset.
 *
 * Operates on a card array directly (not a Player) so callers can apply it
 * to already-filtered hand subsets inside their setGameState reducer.
 */
export function resetHeadhunterDiscounts(cards: Card[]): Card[] {
  return cards.map(c => {
    if (!c._headhunterDiscounted) return c;
    return { ...c, apCost: c.apCost + 1, _headhunterDiscounted: false };
  });
}

/** Base inventory cap — keep in sync with hardcoded UI checks. */
const BASE_INVENTORY_CAP = 4;

/**
 * Player inventory cap including Resourceful bonus. Returns 4 normally,
 * 5 when Resourceful is owned.
 */
export function getInventoryCap(run: RunState | null | undefined): number {
  return BASE_INVENTORY_CAP + (hasPassive(run, 'resourceful') ? 1 : 0);
}

// --- Background bucket: signal multipliers ---

export type SignalOrigin = 'movement' | 'idle' | 'misdirect' | 'transit';

export interface PassiveSignalContext {
  /** Edges in the movement path (for movement/misdirect/transit signals). */
  edges?: NavEdge[];
  /** Nearest edge for idle signals. */
  nearestEdge?: NavEdge | null;
  /** Whether the player is currently in a vehicle (for Wheelman). */
  isInVehicle?: boolean;
  /** True if no other signal has been generated yet this turn (for First Mover Advantage). */
  isFirstSignalThisTurn?: boolean;
  /** What kind of signal this is. Method Actor only boosts 'misdirect'. */
  origin: SignalOrigin;
}

/**
 * Compute a multiplier to apply to a player-generated signal based on the
 * owned passives. Multiple passives stack multiplicatively per the design doc.
 *
 * Multipliers (all applied when their gating condition is met):
 *   - Burned Face        × 0.75 (flat, all road types)
 *   - Bystander          × (1 - 0.4 × commercialFraction)
 *   - Neighbor           × (1 - 0.25 × residentialFraction)
 *   - Wheelman           × 0.75 (when in vehicle)
 *   - First Mover Adv.   × 0.5 (on the first signal of the turn)
 *   - Method Actor       × 1.25 (only on misdirect signals — stronger = tighter)
 *
 * Returns 1 (no change) when run is null or no relevant passives are owned.
 */
export function computePassiveSignalMultiplier(
  context: PassiveSignalContext,
  run: RunState | null | undefined,
): number {
  if (!run || !run.passives || run.passives.length === 0) return 1;

  let mult = 1;

  if (hasPassive(run, 'burnedFace')) mult *= 0.75;

  if (hasPassive(run, 'bystander')) {
    const commercial = computeZoneFraction(context, 'commercial');
    mult *= 1 - commercial * 0.4;
  }

  if (hasPassive(run, 'neighbor')) {
    const residential = computeZoneFraction(context, 'residential');
    mult *= 1 - residential * 0.25;
  }

  if (context.isInVehicle && hasPassive(run, 'wheelman')) {
    mult *= 0.75;
  }

  if (context.isFirstSignalThisTurn && hasPassive(run, 'firstMoverAdvantage')) {
    mult *= 0.5;
  }

  if (context.origin === 'misdirect' && hasPassive(run, 'methodActor')) {
    mult *= 1.25;
  }

  return mult;
}

/**
 * What fraction (0..1) of the signal source is in the given zone.
 * - For idle signals, this is 1 if the nearest edge is in the zone, else 0.
 * - For path-based signals, this is the length-weighted fraction of edges
 *   in that zone.
 */
function computeZoneFraction(context: PassiveSignalContext, zone: string): number {
  if (context.nearestEdge !== undefined) {
    return context.nearestEdge && context.nearestEdge.zone === zone ? 1 : 0;
  }
  if (context.edges && context.edges.length > 0) {
    let totalLength = 0;
    let zoneLength = 0;
    for (const edge of context.edges) {
      totalLength += edge.length;
      if (edge.zone === zone) zoneLength += edge.length;
    }
    return totalLength > 0 ? zoneLength / totalLength : 0;
  }
  return 0;
}
